import { Fragment } from 'react'

import { InlineText } from '@/components/ui/inlineField'
import {
  type AccountDetails,
  NUMBER_MASK,
} from '@/modules/projects/components/Workbench/accounts'
import { Fact } from '@/modules/projects/components/Workbench/Fact'
import { readableName } from '@/modules/projects/components/Workbench/readableName'
import type { Account } from '@/modules/projects/components/Workbench/types'
import { TypeField } from './TypeField'

interface AccountIdentityProps {
  account: Account
  onEdit: (details: AccountDetails) => void
}

// The facts that name the account, as rows for the column's list. The number carries a little
// more weight than the rest because the map, the strip and the scrubber all call the account by it.
export const AccountIdentity = ({ account, onEdit }: AccountIdentityProps) => {
  const { numbers, accountType, owner, institution } = account

  const change = (details: Partial<AccountDetails>) =>
    onEdit({ numbers, accountType, owner, institution, ...details })

  // Emptying a number takes it off the mailing, so no button has to stand beside the digits.
  const commitNumber = (index: number, typed: string) => {
    const kept = typed
      ? numbers.map((number, at) => (at === index ? typed : number))
      : numbers.filter((_unused, at) => at !== index)
    change({ numbers: kept.length > 0 ? kept : numbers })
  }

  return (
    <>
      <Fact label="Type">
        <TypeField
          accountType={accountType}
          onPick={(picked) => change({ accountType: picked })}
        />
      </Fact>

      <Fact label={numbers.length > 1 ? 'Numbers' : 'Number'}>
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 font-medium text-foreground">
          {numbers.map((number, index) => (
            <Fragment key={`${index}-${number}`}>
              {index > 0 ? (
                <span className="font-normal text-ink-subtle">+</span>
              ) : null}
              <span className="flex items-baseline whitespace-nowrap tabular-nums">
                <span className="mr-0.5 font-normal text-ink-subtle">
                  {NUMBER_MASK}
                </span>
                <InlineText
                  value={number}
                  placeholder="0000"
                  title="Correct this number everywhere it is filed. Empty it to take it off the mailing."
                  className="font-medium tabular-nums"
                  openWidth="w-16"
                  onCommit={(typed) => commitNumber(index, typed)}
                  dataAttribute={{ 'data-account-number-input': index }}
                />
              </span>
            </Fragment>
          ))}
        </span>
      </Fact>

      <Fact label="Bank">
        <InlineText
          value={institution ?? ''}
          shown={readableName(institution ?? '')}
          placeholder="Add the bank"
          title="Correct the bank on the letterhead everywhere it is filed"
          className="text-foreground"
          onCommit={(typed) => change({ institution: typed || null })}
          dataAttribute={{ 'data-account-institution-input': '' }}
        />
      </Fact>

      <Fact label="Owner">
        <InlineText
          value={owner ?? ''}
          shown={readableName(owner ?? '')}
          placeholder="Add the owner"
          title="Correct the owner everywhere it is filed"
          className="text-foreground"
          onCommit={(typed) => change({ owner: typed || null })}
          dataAttribute={{ 'data-account-owner-input': '' }}
        />
      </Fact>
    </>
  )
}
